import React, {FC, useEffect, useMemo, useRef} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button,  Input, Label, Spinner, Alert } from "reactstrap";
import { actions } from "~/state/reducers/api-key-reducer";
import Selectors from "~/state/selectors";
import useEffectOnce from "~/hooks/use-effect-once.ts";
import { ApiKeyInfo } from "~/types/api-key-type.ts";
import * as yup from "yup";
import {Formik, Form as FormikForm, type FormikHelpers, FormikProps} from "formik";

/**
 * Extracts the username from a URN the username is after the last ":".
 * Examples:
 *  "urn:o:TN:anr:TNIS:u:jdoe" -> "jdoe"
 *  "jdoe" -> "jdoe"
 */
const usernameFromUrn = (urn?: string | null): string => {
    if (!urn) return "";

    const s = urn.trim();
    if (!s) return "";

    const idx = s.lastIndexOf(":");
    return idx === -1 ? s : s.slice(idx + 1).trim();
};

const formatMaskedApiKey = (maskedKey: string, maskLength: number = 40): string => {
    if (!maskedKey) return "";
    const last4 = maskedKey.slice(-4);
    return `${"*".repeat(maskLength)}${last4}`;
};


type FormValues = {
    apiKey: string;
};

const schema = yup.object({
    apiKey: yup
        .string()
        .trim()
        .required("ERP API Key is required.")
});

const ApiKeyPage: FC = () => {
    const dispatch = useDispatch();
    const apiKeyInfo : ApiKeyInfo = useSelector(Selectors.getApiKeyInfo);
    const meta = useSelector(Selectors.getApiKeyMeta)
    const errorMsg: string = useSelector(Selectors.getApiKeyError);

    const formikRef = useRef<FormikProps<FormValues>>(null);
    const apiKeyInputRef = useRef<HTMLInputElement>(null);
    const prevUpdatingRef = useRef<boolean>(false);

    const loading = Boolean(meta?.fetchingApiKey);
    const updating = Boolean(meta?.updatingApiKey);

    const forbidden = useSelector(Selectors.getApiKeyForbidden)
    const notSupported = useSelector(Selectors.getApiKeyNotSupported)

    useEffectOnce(() => {
        dispatch(actions.fetchApiKey())
    });

    useEffect(() => {
        const wasUpdating = prevUpdatingRef.current;
        if (wasUpdating && !updating) {
            if (!errorMsg) {
                formikRef.current?.resetForm({
                    values: { apiKey: formatMaskedApiKey(apiKeyInfo?.apiKeyMasked ?? "") }
                });
            } else {
                requestAnimationFrame(() => {
                    apiKeyInputRef.current?.focus();
                });
            }
        }
        prevUpdatingRef.current = updating;
    }, [updating, errorMsg, apiKeyInfo?.apiKeyMasked]);

    const initialValues = useMemo<FormValues>(() => ({
        apiKey: formatMaskedApiKey(apiKeyInfo?.apiKeyMasked ?? "",)
    }), [apiKeyInfo?.apiKeyMasked]);

    const lastModifiedDate = apiKeyInfo?.apiKeyModifiedDateMillis ?
        new Date(apiKeyInfo.apiKeyModifiedDateMillis).toLocaleString()
        : "";

    const lastModifiedBy = apiKeyInfo?.apiKeyModifiedBy ?
        usernameFromUrn(apiKeyInfo.apiKeyModifiedBy)
        : "";

    const onSubmit = (values: FormValues, helpers: FormikHelpers<FormValues>) => {
        dispatch(actions.updateApiKey({ apiKey: values.apiKey }));
        helpers.setSubmitting(false);
    };


    if (forbidden) {
        return (
            <React.Fragment>
                <Alert color="danger">
                    You do not have permission to view or update ERP API Key. Please contact your administrator.
                </Alert>
            </React.Fragment>
        )
    }
    if (notSupported) {
        return (
            <React.Fragment>
                <Alert color="danger">
                    ERP API Key management is not supported yet. Please contact your administrator.
                </Alert>
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            { loading ? (
                <div className="d-flex justify-content-center align-items-center vh-100">
                    <Spinner size="sm" />
                </div>
                ) : (
                <Formik<FormValues>
                    enableReinitialize={false}
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={schema}
                    onSubmit={onSubmit}
                    validateOnMount={true}
                >
                    {(formik) => {
                        const {values, errors, touched, handleChange } = formik;
                        const showYupError =  Boolean(touched.apiKey && errors.apiKey);
                        const showServerError = Boolean(errorMsg)

                        const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                            if (errorMsg) dispatch(actions.clearApiKeyError());
                            handleChange(e);
                        }

                        return (
                            <FormikForm>
                                <div className="container-fluid px-2"
                                style={{
                                    fontFamily: "'Trebuchet MS', Arial, Helvetica, sans-serif",
                                    fontSize: "small",
                                }}
                                >
                                    <div className="ms-0 me-auto" style={{ maxWidth: 980}}>
                                        <div className="row align-items-start g-2 mt-3">
                                            <div className="col-auto">
                                                <Label htmlFor="erpApiKey" className="fw-semibold mb-0">ERP API Key</Label>
                                            </div>
                                            <div className="col">
                                                <div className="input-group w-100" style={{ maxWidth: 420}}>
                                                    <Input
                                                        id="apiKey"
                                                        name="apiKey"
                                                        innerRef={apiKeyInputRef}
                                                        value={values.apiKey}
                                                        disabled={loading || updating}
                                                        onChange={handleApiKeyChange}
                                                        className="api-key-input form-control text-truncate"
                                                        aria-describedby="apiKeyMessage"
                                                    />
                                                    {loading && (
                                                        <div className="mx-2">
                                                            <Spinner size="sm" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div
                                                    id="apiKeyMessage"
                                                    className={(showYupError || showServerError) ? "text-danger small" : "text-muted small"}
                                                    style={{ minHeight: "1.25rem"}}
                                                >
                                                    {showYupError
                                                        ? errors.apiKey
                                                        : showServerError
                                                            ? errorMsg
                                                            : "Enter a valid API Key to save changes."
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row align-items-center gap-3 mt-3">
                                            <div className="col-12 col-md-auto">
                                                <Button
                                                    type="submit"
                                                    className="gwt-like-btn"
                                                    disabled={loading || updating}
                                                >
                                                    {updating ? (
                                                        <>
                                                            <Spinner size="sm" /> Saving...
                                                        </>
                                                    ) : (
                                                        "Save"
                                                    )}
                                                </Button>
                                            </div>

                                            <div className="col-12 col-sm-6 col-md-auto text-muted">
                                                <span className="fw-semibold">Last modified: </span> {lastModifiedDate || "-"}
                                            </div>
                                            <div className="col-12 col-sm-6 col-md-auto text-muted">
                                                <span className="fw-semibold">Modified by: </span> {lastModifiedBy || "-"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </FormikForm>
                        )
                    }}
                </Formik>
            )
            }
        </React.Fragment>
    );
};

export default ApiKeyPage;
